import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import {
  CandidatePathSchema,
  CandidatePoolPathSchema,
  MessagePathSchema,
  PaymentMethodPathSchema,
  PaymentPathSchema,
  SchedulePathSchema,
  SearchResultPathSchema,
  SearchSessionPathSchema,
  ThreadPathSchema,
  TravelerPathSchema,
  TripPathSchema,
  UserPathSchema,
} from "@generated/proto/model";

import {
  camelToSnake,
  remapKeysToCamel,
  remapKeysToSnake,
} from "./stringCase";

/**
 * Create a protobuf message from a Firestore document data object by
 * converting snake_case keys to lowerCamelCase expected by TS/JSON mapping.
 */
export function createProtoFromFirestore<T>(schema: unknown, raw: unknown): T {
  return create(schema as any, remapKeysToCamel(raw) as any) as T;
}

/**
 * Utility to map Firestore doc data to a plain JS object with camelCase keys.
 * Useful when you don't need a full proto, but want consistent key shapes.
 */
export function fromFirestoreToCamel<T = any>(raw: unknown): T {
  return remapKeysToCamel(raw) as T;
}

/**
 * Convert a protobuf message to Firestore format by converting camelCase keys to snake_case.
 * This is the inverse of createProtoFromFirestore.
 */
export function protoToFirestore<T = any>(proto: unknown): T {
  return remapKeysToSnake(proto) as T;
}

export function makeUserPath(userId: string) {
  return create(UserPathSchema, { userId });
}

export function makeTripPath(userId: string, tripId: string) {
  return create(TripPathSchema, { userId, tripId });
}

export function makeFieldMask(paths: string[]) {
  return create(FieldMaskSchema, { paths: fieldMaskFromKeys(paths) });
}

export function makeSchedulePath(
  userId: string,
  tripId: string,
  scheduleId: string
) {
  return create(SchedulePathSchema, { userId, tripId, scheduleId });
}

export function makeTravelerPath(userId: string, travelerId: string) {
  return create(TravelerPathSchema, { userId, travelerId });
}

export function makeSearchSessionPath(userId: string, tripId: string, searchSessionId: string) {
  return create(SearchSessionPathSchema, { userId, tripId, searchSessionId });
}

export function makeSearchResultPath(
  userId: string,
  tripId: string,
  searchSessionId: string,
  searchResultId: string
) {
  return create(SearchResultPathSchema, {
    userId,
    tripId,
    searchSessionId,
    searchResultId,
  });
}

export function makeThreadPath(userId: string, tripId: string, threadId: string) {
  return create(ThreadPathSchema, { userId, tripId, threadId });
}

export function makeMessagePath(userId: string, tripId: string, threadId: string, messageId: string) {
  return create(MessagePathSchema, { userId, tripId, threadId, messageId });
}

export function makePaymentPath(userId: string, tripId: string, paymentId: string) {
  return create(PaymentPathSchema, { userId, tripId, paymentId });
}

export function makePaymentMethodPath(userId: string, paymentMethodId: string) {
  return create(PaymentMethodPathSchema, { userId, paymentMethodId });
}

export function makeCandidatePoolPath(
  userId: string,
  tripId: string,
  candidatePoolId: string
) {
  return create(CandidatePoolPathSchema, { userId, tripId, candidatePoolId });
}

export function makeCandidatePath(
  userId: string,
  tripId: string,
  candidatePoolId: string,
  candidateId: string
) {
  return create(CandidatePathSchema, {
    userId,
    tripId,
    candidatePoolId,
    candidateId,
  });
}

function fieldMaskFromKeys<T extends object>(
  keys: Array<Extract<keyof T, string | number>>
): string[] {
  return keys.map((k) => camelToSnake(String(k)));
}
